import { useState } from 'react'
import { motion } from 'framer-motion'
import { ReactNode } from 'react'

interface FlipCardProps {
  front: ReactNode
  back: ReactNode
  className?: string
  frontClassName?: string
  backClassName?: string
}

export default function FlipCard({
  front,
  back,
  className = '',
  frontClassName = '',
  backClassName = '',
}: FlipCardProps) {
  const [isFlipped, setIsFlipped] = useState(false)

  return (
    <motion.div
      className={`relative cursor-pointer perspective-1000 ${className}`}
      onClick={() => setIsFlipped(!isFlipped)}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="relative w-full h-full"
        style={{ transformStyle: 'preserve-3d' }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Front */}
        <div
          className={`absolute inset-0 backface-hidden ${frontClassName}`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          {front}
          <div className="absolute bottom-3 right-3 text-xs text-white/40">
            Click to flip
          </div>
        </div>

        {/* Back */}
        <div
          className={`absolute inset-0 backface-hidden ${backClassName}`}
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          {back}
          <div className="absolute bottom-3 right-3 text-xs text-white/40">
            Click to flip back
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

// Expandable card variant
export function ExpandableCard({
  title,
  preview,
  expanded,
  icon,
  className = '',
}: {
  title: string
  preview: string
  expanded: ReactNode
  icon?: ReactNode
  className?: string
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <motion.div
      layout
      className={`glass rounded-2xl overflow-hidden cursor-pointer ${className}`}
      onClick={() => setIsExpanded(!isExpanded)}
      whileHover={{ scale: isExpanded ? 1 : 1.02 }}
    >
      <motion.div layout="position" className="p-6">
        <div className="flex items-start gap-4">
          {icon && (
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-electric-cyan/20 to-electric-purple/20 flex items-center justify-center">
              {icon}
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
            <p className="text-white/60 text-sm">{preview}</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={false}
        animate={{
          height: isExpanded ? 'auto' : 0,
          opacity: isExpanded ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="px-6 pb-6 pt-2 border-t border-white/10">
          {expanded}
        </div>
      </motion.div>

      <div className="absolute top-4 right-4">
        <motion.span
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="inline-block text-white/40"
        >
          ▼
        </motion.span>
      </div>
    </motion.div>
  )
}
